"use client"

import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Users, UserPlus, Settings, Shield, FileText, ShieldCheck } from 'lucide-react'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'
import TeamDirectory from './TeamDirectory'
import TeamInvitations from './TeamInvitations'
import TeamRoles from './TeamRoles'
import TeamPermissions from './TeamPermissions'
import TeamPolicies from './TeamPolicies'
import TeamSecurity from './TeamSecurity'

// Team Management main component with tabbed interface
export function TeamManagement() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('directory')
  const tabNavRef = useRef(null)

  // Initialize active tab from URL params
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['directory', 'invitations', 'roles', 'permissions', 'policies', 'security'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])

  // Scroll active tab into view on mobile
  useEffect(() => {
    if (tabNavRef.current) {
      const activeButton = tabNavRef.current.querySelector('.team-tab-button.active')
      if (activeButton) {
        // Smooth scroll to ensure active tab is visible
        activeButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        })
      }
    }
  }, [activeTab])

  // Update URL when tab changes and ensure visibility
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  // Tab configuration
  const tabs = [
    {
      id: 'directory',
      label: 'Directory',
      icon: Users,
      description: 'Team member directory and management',
      component: <TeamDirectory />
    },
    {
      id: 'invitations',
      label: 'Invitations',
      icon: UserPlus,
      description: 'Manage team invitations and onboarding',
      component: <TeamInvitations />
    },
    {
      id: 'roles',
      label: 'Roles',
      icon: Shield,
      description: 'Role hierarchy and assignments',
      component: <TeamRoles />
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: ShieldCheck,
      description: 'Permission matrix and management',
      component: <TeamPermissions />
    },
    {
      id: 'policies',
      label: 'Policies',
      icon: FileText,
      description: 'Policy bundles and configuration',
      component: <TeamPolicies />
    },
    {
      id: 'security',
      label: 'Security',
      icon: Settings,
      description: 'Team security dashboard',
      component: <TeamSecurity />
    }
  ]

  const currentTab = tabs.find(tab => tab.id === activeTab) || tabs[0]

  return (
    <div className="team-management mobile-full-width">
      {/* Header */}
      <div className="mb-6 mobile-full-width">
        <h1 className="text-3xl font-bold text-primary mb-2">Team Management</h1>
        <p className="text-muted">
          Manage your team, roles, permissions, and security settings
        </p>
      </div>

      {/* Tab Navigation - Mobile Responsive */}
      <div className="team-tab-nav mobile-full-width">
        <nav ref={tabNavRef} role="tablist" aria-label="Team management sections">
          {tabs.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`team-tab-button ${isActive ? 'active' : ''}`}
                aria-label={`${tab.label} tab`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tab-panel-${tab.id}`}
                id={`tab-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
              >
                <Icon size={16} aria-hidden="true" />
                <span>{tab.label}</span>
                <VisuallyHidden>
                  {isActive 
                    ? `Currently viewing ${tab.label}. ${tab.description}` 
                    : `Switch to ${tab.label}. ${tab.description}`
                  }
                </VisuallyHidden>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content mobile-full-width">
        <div className="mb-4 mobile-full-width">
          <h2 className="text-xl font-semibold text-primary mb-1">
            {currentTab.label}
          </h2>
          <p className="text-sm text-muted">
            {currentTab.description}
          </p>
        </div>
        
        <div 
          className="tab-panel mobile-full-width"
          role="tabpanel"
          id={`tab-panel-${currentTab.id}`}
          aria-labelledby={`tab-${currentTab.id}`}
          tabIndex={0}
        >
          {currentTab.component}
        </div>
      </div>
    </div>
  )
}

export default TeamManagement